import React from 'react';
import LocalFilterPanel from '../../../components/common/LocalFilterPanel';
import TileEventAttributeFilter from './components/TileEventAttributeFilter';
import { useOcelMetadata } from '../../../stores/OcelContext';
import {
  SectionHeader,
  ActivityFilterBlock,
  ComparisonSection,
  BetrachtungszeitraumSection,
} from '../_shared/PeriodSettingsControls';

const LiefertermintreueKPISettings = ({
  kpiTitle,
  onKpiTitleChange,
  objectType,
  onObjectTypeChange,
  refDateType,
  onRefDateTypeChange,
  refDateValue,
  onRefDateValueChange,
  actualDateType,
  onActualDateTypeChange,
  actualDateValue,
  onActualDateValueChange,
  tileEventAttributeFilter,
  onTileEventAttributeFilterChange,
  objectTypes,
  activitiesForType,
  dateAttrsForType,
  loading = false,
  activitiesLoading = false,
  dateAttrsLoading = false,
  localFilters,
  updateLocalFilter,
  activeFile,
  // Betrachtungszeitraum / Vorperioden-Vergleich (unscoped activities list —
  // not activitiesForType, which is objectType-scoped for the date pickers above)
  availableActivities,
  periodPreset,
  onPeriodPresetChange,
  customStart,
  onCustomStartChange,
  customEnd,
  onCustomEndChange,
  showPeriodLabel,
  onShowPeriodLabelChange,
  showExtraStats,
  onShowExtraStatsChange,
  comparisonEnabled,
  onComparisonEnabledChange,
  trendDirection,
  onTrendDirectionChange,
}) => {
  const metadata = useOcelMetadata(activeFile);
  const datasetMin = metadata?.date_range?.start ? metadata.date_range.start.split('T')[0] : undefined;
  const datasetMax = metadata?.date_range?.end ? metadata.date_range.end.split('T')[0] : undefined;

  const objectTypeList = Array.isArray(objectTypes)
    ? objectTypes
    : Object.keys(objectTypes || {});

  const renderDateValueSelect = (dateType, dateValue, onDateValueChange) => {
    const isDisabled = loading || !objectType;
    if (dateType === 'activity_timestamp') {
      return (
        <select
          value={dateValue}
          onChange={(e) => onDateValueChange(e.target.value)}
          disabled={isDisabled || activitiesLoading}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          {!objectType ? (
            <option value="">Select object type first…</option>
          ) : (
            <option value="">Select activity…</option>
          )}
          {activitiesForType.map((act) => (
            <option key={act} value={act}>{act}</option>
          ))}
        </select>
      );
    }
    return (
      <select
        value={dateValue}
        onChange={(e) => onDateValueChange(e.target.value)}
        disabled={isDisabled || dateAttrsLoading}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
      >
        {!objectType ? (
          <option value="">Select object type first…</option>
        ) : dateAttrsLoading ? (
          <option value="">Loading attributes…</option>
        ) : dateAttrsForType.length === 0 ? (
          <option value="">No date attributes found</option>
        ) : (
          <option value="">Select date attribute…</option>
        )}
        {dateAttrsForType.map((attr) => (
          <option key={attr} value={attr}>{attr}</option>
        ))}
      </select>
    );
  };

  return (
    <div className="space-y-6">
      {/* KPI Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">KPI Title *</label>
        <input
          type="text"
          value={kpiTitle}
          onChange={(e) => onKpiTitleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g. Delivery Date Compliance"
          disabled={loading}
        />
      </div>

      {/* Object Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Object Type *</label>
        <select
          value={objectType}
          onChange={(e) => onObjectTypeChange(e.target.value)}
          disabled={loading}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select object type…</option>
          {objectTypeList.map((ot) => (
            <option key={ot} value={ot}>{ot}</option>
          ))}
        </select>
      </div>

      {/* Reference Date */}
      <div className="space-y-3 p-4 border border-gray-200 rounded-lg">
        <h4 className="text-sm font-semibold text-gray-800">Reference Date</h4>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Type *</label>
          <select
            value={refDateType}
            onChange={(e) => onRefDateTypeChange(e.target.value)}
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="object_attribute">Object Attribute</option>
            <option value="activity_timestamp">Activity Timestamp</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {refDateType === 'activity_timestamp' ? 'Activity *' : 'Attribute *'}
          </label>
          {renderDateValueSelect(refDateType, refDateValue, onRefDateValueChange)}
        </div>
      </div>

      {/* Actual Date */}
      <div className="space-y-3 p-4 border border-gray-200 rounded-lg">
        <h4 className="text-sm font-semibold text-gray-800">Actual Date</h4>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Type *</label>
          <select
            value={actualDateType}
            onChange={(e) => onActualDateTypeChange(e.target.value)}
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="object_attribute">Object Attribute</option>
            <option value="activity_timestamp">Activity Timestamp</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {actualDateType === 'activity_timestamp' ? 'Activity *' : 'Attribute *'}
          </label>
          {(() => {
            const isDisabled = loading || !objectType;
            const isLoading = actualDateType === 'activity_timestamp' ? activitiesLoading : dateAttrsLoading;
            const items = actualDateType === 'activity_timestamp' ? activitiesForType : dateAttrsForType;
            return (
              <div
                className="w-full border border-gray-300 rounded-md overflow-y-auto h-28"
                style={{ opacity: (isDisabled || isLoading) ? 0.5 : 1, pointerEvents: (isDisabled || isLoading) ? 'none' : 'auto' }}
              >
                {!objectType && <p className="px-3 py-2 text-sm text-gray-400">Select object type first…</p>}
                {objectType && isLoading && <p className="px-3 py-2 text-sm text-gray-400">Loading…</p>}
                {objectType && !isLoading && items.length === 0 && <p className="px-3 py-2 text-sm text-gray-400">No options found</p>}
                {items.map((item) => (
                  <div
                    key={item}
                    onClick={() => {
                      const next = actualDateValue.includes(item)
                        ? actualDateValue.filter((a) => a !== item)
                        : [...actualDateValue, item];
                      onActualDateValueChange(next);
                    }}
                    className={`px-3 py-1.5 cursor-pointer text-sm select-none ${actualDateValue.includes(item) ? 'bg-primary-500 text-white' : 'hover:bg-gray-100'}`}
                  >
                    {item}
                  </div>
                ))}
              </div>
            );
          })()}
          <p className="text-xs text-gray-500 mt-1">Mehrere Attribute auswählbar. Frühestes Datum wird verwendet.</p>
        </div>
      </div>

      <div className="border-t border-gray-200" />

      {/* Betrachtungszeitraum */}
      <BetrachtungszeitraumSection
        periodPreset={periodPreset}
        onPeriodPresetChange={onPeriodPresetChange}
        customStart={customStart}
        onCustomStartChange={onCustomStartChange}
        customEnd={customEnd}
        onCustomEndChange={onCustomEndChange}
        datasetMin={datasetMin}
        datasetMax={datasetMax}
        comparisonEnabled={comparisonEnabled}
      />

      {/* Periodenbezeichnung anzeigen */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={showPeriodLabel}
          onChange={(e) => onShowPeriodLabelChange(e.target.checked)}
          disabled={loading}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700">Periodenbezeichnung anzeigen</span>
      </label>

      {/* Zusatzinfos (On Time / Late / Evaluated) anzeigen */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={showExtraStats}
          onChange={(e) => onShowExtraStatsChange(e.target.checked)}
          disabled={loading}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700">Zusatzinfos anzeigen (On Time / Late / Evaluated)</span>
      </label>

      {/* Vorperioden-Vergleich */}
      <ComparisonSection
        periodPreset={periodPreset}
        comparisonEnabled={comparisonEnabled}
        onComparisonEnabledChange={onComparisonEnabledChange}
        trendDirection={trendDirection}
        onTrendDirectionChange={onTrendDirectionChange}
        loading={loading}
        radioGroupName="liefertermintreueKpiTrendDirection"
      />

      {/* Activity Filter — within time scope */}
      <div>
        <SectionHeader
          title="Aktivitäten im Betrachtungszeitraum"
          subtitle="Nur Objekte, die die gewählten Aktivitäten innerhalb des oben gewählten Zeitraums ausgeführt haben."
        />
        <div className="mt-3">
          <ActivityFilterBlock
            filterKey="activityFilters"
            localFilters={localFilters}
            updateLocalFilter={updateLocalFilter}
            availableActivities={availableActivities}
            loading={loading}
          />
        </div>
      </div>

      {/* Activity Filter — full lifecycle */}
      <div>
        <SectionHeader
          title="Aktivitäten im Lebenszyklus"
          subtitle="Nur Objekte, die diese Aktivitäten jemals in ihrer gesamten Geschichte hatten — unabhängig vom Betrachtungszeitraum."
        />
        <div className="mt-3">
          <ActivityFilterBlock
            filterKey="lifecycleActivityFilters"
            localFilters={localFilters}
            updateLocalFilter={updateLocalFilter}
            availableActivities={availableActivities}
            loading={loading}
          />
        </div>
      </div>

      <div className="border-t border-gray-200" />

      {/* Tile-local Event Attribute Filter */}
      <TileEventAttributeFilter
        value={tileEventAttributeFilter}
        onChange={onTileEventAttributeFilterChange}
        activeFile={activeFile}
        disabled={loading}
      />

      <div className="border-t border-gray-200" />

      {/* Standard local filters — dateRange removed: the Betrachtungszeitraum
          above now owns that role (avoids the "two competing date pickers"
          bug fixed in Aggregation KPI). objectTypes removed: redundant with
          the required single "Object Type" dropdown above. activityNodes
          removed: redundant with the two Aktivitäten-Blöcke above. Only
          objectAttributes has no equivalent elsewhere, so it stays. */}
      <LocalFilterPanel
        localFilters={localFilters}
        updateLocalFilter={updateLocalFilter}
        activeFile={activeFile}
        disabled={loading}
        enabledFilterTypes={['objectAttributes']}
      />
    </div>
  );
};

export default LiefertermintreueKPISettings;
